<template>
  <div class="user-detail">
    <div class="detail-header">
      <button class="back-button" @click="$router.push('/dashboard')">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back to Dashboard
      </button>
    </div>

    <div v-if="loading" class="loading-state full-page">
      <div class="cosmic-loader"></div>
      <span>Loading user data...</span>
    </div>
    
    <div v-else-if="!userData" class="error-state full-page">
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <h2>User Not Found</h2>
      <p>The user you're looking for doesn't exist or has been deleted.</p>
      <button class="back-link" @click="$router.push('/dashboard')">Return to Dashboard</button>
    </div>

    <div v-else class="detail-content">
      <div class="user-info-card">
        <div class="user-info-header">
          <div class="user-details">
            <h2>{{ userData?.name || 'Unknown User' }}</h2>
            <span class="user-meta">Created: {{ formatCreatedAt(userData?.createdAt) }}</span>
          </div>
        </div>

        <div class="info-section">
          <h3>Device Information</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">Device</span>
              <span class="value">{{ userData?.model || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="label">OS Version</span>
              <span class="value">{{ userData?.systemVersion || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Language</span>
              <span class="value">{{ userData?.language || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Time Zone</span>
              <span class="value">{{ userData?.timeZone || '-' }}</span>
            </div>
          </div>
        </div>

        <div class="info-section">
          <h3>Account Details</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">User ID</span>
              <div class="user-id-container">
                <span class="user-id">{{ userData?.uid || '-' }}</span>
                <button class="copy-btn" @click="copyUserId(userData?.uid)" title="Copy User ID">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div class="info-item">
              <span class="label">Registration Date</span>
              <span class="value">{{ formatCreatedAt(userData?.createdAt) }}</span>
            </div>
          </div>
        </div>

        <div class="info-section">
          <div class="section-header">
            <h3>Premium Status</h3>
            <div v-if="premiumStatus && !isEditingPremium" class="action-buttons">
              <button class="edit-btn" @click="startEditPremium">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Edit
              </button>
              <button v-if="premiumStatus.isPremium" class="revoke-btn" @click="revokePremium">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                Revoke
              </button>
            </div>
          </div>

          <div v-if="!isEditingPremium">
            <div v-if="premiumStatus" class="info-grid">
              <div class="info-item">
                <span class="label">Status</span>
                <span class="value" :class="{ 'premium-active': premiumStatus.isPremium }">
                  {{ premiumStatus.isPremium ? '✓ Premium' : 'Free' }}
                </span>
              </div>
              <div class="info-item" v-if="premiumStatus.isPremium">
                <span class="label">Expires At</span>
                <span class="value">{{ premiumStatus.expiresAt || '-' }}</span>
              </div>
              <div class="info-item" v-if="premiumStatus.isPremium">
                <span class="label">Access Level</span>
                <span class="value">{{ premiumStatus.accessLevel || '-' }}</span>
              </div>
            </div>
            <div v-else class="info-grid">
              <div class="info-item">
                <span class="label">Status</span>
                <span class="value">Loading...</span>
              </div>
            </div>
          </div>

          <div v-else class="edit-form">
            <div class="form-group">
              <label for="expiresAt">Expires At</label>
              <input 
                id="expiresAt" 
                type="date" 
                v-model="editPremiumData.expiresAt"
                class="form-input"
              >
            </div>
            <div class="form-actions">
              <button class="btn-save" @click="savePremiumChanges">Save</button>
              <button class="btn-cancel" @click="cancelEditPremium">Cancel</button>
            </div>
          </div>
        </div>

        <div class="info-section">
          <h3>Scan Statistics</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">Total Scans</span>
              <span class="value">{{ scans.length }}</span>
            </div>
            <div class="info-item">
              <span class="label">Last Scan</span>
              <span class="value">{{ formatLastScanDate() }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="history-content">
        <div class="history-switcher">
          <button :class="{ active: activeTab === 'scans' }" @click="activeTab = 'scans'">
            Scan History ({{ scans.length }})
          </button>
          <button :class="{ active: activeTab === 'chats' }" @click="activeTab = 'chats'">
            Chat History ({{ chats.length }})
          </button>
        </div>

        <div v-if="activeTab === 'scans'" class="scans-history">
          <div v-if="loading" class="loading-state">
            <div class="cosmic-loader"></div>
            <span>Loading scan history...</span>
          </div>
          <div v-else class="scans-container">
            <div v-for="scan in scans" :key="scan.scanId" class="scan-card">
              <div class="scan-header">
                <div class="scan-info">
                  <h4>{{ scan.data?.productName || 'N/A' }}</h4>
                  <span class="scan-date">{{ formatCreatedAt(scan.createdAt) }}</span>
                </div>
                <div class="scan-status">
                  <span class="status-badge" :class="scan.status">{{ scan.status }}</span>
                </div>
              </div>
              
              <div class="scan-content">
                <div class="scan-main-info">
                  <div class="scan-basic-details">
                    <div class="scan-barcode">
                      <strong>Barcode:</strong> {{ scan.barcode || scan.data?.barcode || 'N/A' }}
                    </div>
                    
                    <div class="scan-brand">
                      <strong>Brand:</strong> {{ scan.data?.brand || 'N/A' }}
                    </div>

                    <div class="scan-quantity">
                      <strong>Quantity:</strong> {{ scan.data?.quantity || 'N/A' }}
                    </div>

                    <div class="scan-packaging">
                      <strong>Packaging:</strong> {{ scan.data?.packaging || 'N/A' }}
                    </div>
                  </div>

                  <div v-if="scan.data?.images?.front" class="scan-image">
                    <img :src="scan.data.images.front" :alt="scan.data?.productName" @error="handleImageError">
                  </div>
                </div>

                <div v-if="scan.aiResponse?.labels && scan.aiResponse.labels.length > 0" class="scan-labels">
                  <strong>AI Labels:</strong>
                  <div class="labels-list">
                    <div v-for="(label, index) in scan.aiResponse.labels" :key="index" class="label-item">
                      <span class="label-text">{{ label.label }}</span>
                      <span class="label-color" :style="{ backgroundColor: getLabelColor(label.color) }"></span>
                    </div>
                  </div>
                </div>

                <div v-if="!scan.expanded" class="scan-preview">
                  <div class="scan-summary">
                    <div class="summary-item">
                      <strong>Allergens:</strong> {{ scan.data?.allergens?.length || 0 }}
                    </div>
                    <div class="summary-item">
                      <strong>Categories:</strong> {{ scan.data?.categories?.length || 0 }}
                    </div>
                    <div class="summary-item">
                      <strong>Ingredients:</strong> {{ scan.data?.ingredients?.length || 0 }}
                    </div>
                  </div>
                  <button class="see-more-btn" @click="expandScan(scan)">
                    See More Details
                  </button>
                </div>

                <div v-else class="scan-details-expanded">
                  <div class="scan-details-grid">
                    <div class="scan-section">
                      <h5>Allergens</h5>
                      <div class="tags-list">
                        <span v-for="allergen in (scan.data?.allergens || [])" :key="allergen" class="tag allergen-tag">{{ allergen }}</span>
                        <span v-if="!scan.data?.allergens?.length" class="no-data">N/A</span>
                      </div>
                    </div>

                    <div class="scan-section">
                      <h5>Categories</h5>
                      <div class="tags-list">
                        <span v-for="category in (scan.data?.categories || [])" :key="category" class="tag category-tag">{{ category }}</span>
                        <span v-if="!scan.data?.categories?.length" class="no-data">N/A</span>
                      </div>
                    </div>

                    <div class="scan-section">
                      <h5>Additives</h5>
                      <div class="tags-list">
                        <span v-for="additive in (scan.data?.additives_original_tags || [])" :key="additive" class="tag additive-tag">{{ additive }}</span>
                        <span v-if="!scan.data?.additives_original_tags?.length" class="no-data">N/A</span>
                      </div>
                    </div>

                    <div class="scan-section">
                      <h5>Traces</h5>
                      <div class="tags-list">
                        <span v-for="trace in (scan.data?.traces || [])" :key="trace" class="tag trace-tag">{{ trace }}</span>
                        <span v-if="!scan.data?.traces?.length" class="no-data">N/A</span>
                      </div>
                    </div>

                    <div class="scan-section">
                      <h5>Product Labels</h5>
                      <div class="tags-list">
                        <span v-for="label in (scan.data?.labels || [])" :key="label" class="tag label-tag">{{ label }}</span>
                        <span v-if="!scan.data?.labels?.length" class="no-data">N/A</span>
                      </div>
                    </div>

                    <div class="scan-section">
                      <h5>Ingredients</h5>
                      <div class="ingredients-list">
                        <div v-for="(ingredient, index) in (scan.data?.ingredients || [])" :key="index" class="ingredient-item">
                          <span class="ingredient-name">{{ ingredient.name }}</span>
                          <span v-if="ingredient.percent" class="ingredient-percent">({{ ingredient.percent }}%)</span>
                          <div class="ingredient-badges">
                            <span v-if="ingredient.vegan === 'yes'" class="badge vegan-badge">Vegan</span>
                            <span v-if="ingredient.vegetarian === 'yes'" class="badge vegetarian-badge">Vegetarian</span>
                          </div>
                        </div>
                        <div v-if="!scan.data?.ingredients?.length" class="no-data">N/A</div>
                      </div>
                    </div>

                    <div class="scan-section">
                      <h5>Nutrition (per 100g)</h5>
                      <div class="nutrition-grid">
                        <div class="nutrition-item">
                          <span class="nutrition-label">Energy:</span>
                          <span class="nutrition-value">{{ scan.data?.nutrition?.energy_kcal || 'N/A' }} {{ scan.data?.nutrition?.energy_kcal ? 'kcal' : '' }}</span>
                        </div>
                        <div class="nutrition-item">
                          <span class="nutrition-label">Fat:</span>
                          <span class="nutrition-value">{{ scan.data?.nutrition?.fat || 'N/A' }} {{ scan.data?.nutrition?.fat ? 'g' : '' }}</span>
                        </div>
                        <div class="nutrition-item">
                          <span class="nutrition-label">Proteins:</span>
                          <span class="nutrition-value">{{ scan.data?.nutrition?.proteins || 'N/A' }} {{ scan.data?.nutrition?.proteins ? 'g' : '' }}</span>
                        </div>
                        <div class="nutrition-item">
                          <span class="nutrition-label">Sugars:</span>
                          <span class="nutrition-value">{{ scan.data?.nutrition?.sugars || 'N/A' }} {{ scan.data?.nutrition?.sugars ? 'g' : '' }}</span>
                        </div>
                        <div class="nutrition-item">
                          <span class="nutrition-label">Salt:</span>
                          <span class="nutrition-value">{{ scan.data?.nutrition?.salt || 'N/A' }} {{ scan.data?.nutrition?.salt ? 'g' : '' }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="scan-section">
                      <h5>Scores</h5>
                      <div class="scores-grid">
                        <div class="score-item">
                          <span class="score-label">NOVA Group:</span>
                          <span class="score-value nova-{{ scan.data?.scores?.novaGroup || 'N/A' }}">{{ scan.data?.scores?.novaGroup || 'N/A' }}</span>
                        </div>
                        <div class="score-item">
                          <span class="score-label">Nutri-Score:</span>
                          <span class="score-value nutri-{{ (scan.data?.scores?.nutriScore || 'N/A').toLowerCase() }}">{{ scan.data?.scores?.nutriScore || 'N/A' }}</span>
                        </div>
                        <div class="score-item">
                          <span class="score-label">Eco-Score:</span>
                          <span class="score-value eco-{{ (scan.data?.scores?.ecoScore || 'N/A').toLowerCase() }}">{{ scan.data?.scores?.ecoScore || 'N/A' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button class="see-less-btn" @click="collapseScan(scan)">
                    See Less
                  </button>
                </div>
              </div>
            </div>
            
            <div v-if="scans.length === 0" class="no-scans">
              No scan history found
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'chats'" class="chat-history">
          <div v-if="loading" class="loading-state">
            <div class="cosmic-loader"></div>
            <span>Loading chat history...</span>
          </div>
          <div v-else class="chats-container">
              <div v-for="chat in chats" :key="chat.chatId" class="chat-session">
                  <div class="chat-messages">
                      <div v-for="message in chat.messages.filter(m => m.role !== 'system')" :key="message.messageId" class="chat-message" :class="`role-${message.role}`">
                          <div class="message-bubble">
                              <p v-html="formatMessageContent(message.content)"></p>
                              <span class="message-time">{{ formatCreatedAt(message.createdAt) }}</span>
                          </div>
                      </div>
                  </div>
              </div>
              <div v-if="chats.length === 0" class="no-chats">
                  No chat history found
              </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { FirebaseUserService } from '../services/firebaseUserService'
import { marked } from 'marked';

export default {
  name: 'UserDetailView',
  setup() {
    const route = useRoute()
    const userId = ref(route.params.id)
    const userService = new FirebaseUserService()
    
    const userData = ref(null)
    const scans = ref([])
    const chats = ref([])
    const loading = ref(true)
    const activeTab = ref('scans')
    const premiumStatus = ref(null)
    const isEditingPremium = ref(false)
    const editPremiumData = ref({
      expiresAt: '',
      startsAt: ''
    })

    const loadData = async () => {
      try {
        loading.value = true
        const result = await userService.getAllUsers()
        
        const user = result.users.find(u => u.uid === userId.value)
        userData.value = user || null
        
        if (userData.value) {
          scans.value = result.allScans
            .filter(scan => scan.userId === userId.value)
            .map(scan => ({ ...scan, expanded: false }))

          chats.value = result.allChats
            .filter(chat => chat.userId === userId.value)

          premiumStatus.value = await userService.checkPremiumStatus(userId.value)
        }

      } catch (error) {
        console.error('Error loading user data:', error)
        userData.value = null
      } finally {
        loading.value = false
      }
    }

    const getUserInitials = (name) => {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    }

    const formatMessageContent = (content) => {
      return marked(content);
    }

    const formatCreatedAt = (createdAt) => {
      if (!createdAt) return '-'
      const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    }

    const formatLastScanDate = () => {
      if (scans.value.length === 0) return '-'
      return formatCreatedAt(scans.value[0].createdAt)
    }

    const getLabelColor = (color) => {
      const colorMap = {
        'red': '#ff4757',
        'yellow': '#ffa502',
        'green': '#2ed573',
        'blue': '#3742fa'
      }
      return colorMap[color] || '#6c757d'
    }

    const copyUserId = async (userId) => {
      if (!userId) return
      try {
        await navigator.clipboard.writeText(userId)
        // You could add a toast notification here
      } catch (error) {
        console.error('Failed to copy user ID:', error)
      }
    }

    const handleImageError = (event) => {
      event.target.style.display = 'none'
    }

    const expandScan = (scan) => {
      scan.expanded = true
    }

    const collapseScan = (scan) => {
      scan.expanded = false
    }

    const formatDateForInput = (dateString) => {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      } catch (error) {
        console.error('Error formatting date:', error)
        return ''
      }
    }

    const startEditPremium = () => {
      if (premiumStatus.value) {
        editPremiumData.value.expiresAt = formatDateForInput(premiumStatus.value.expiresAt)
        editPremiumData.value.startsAt = ''
      }
      isEditingPremium.value = true
    }

    const cancelEditPremium = () => {
      isEditingPremium.value = false
      editPremiumData.value = {
        expiresAt: '',
        startsAt: ''
      }
    }

    const revokePremium = async () => {
      if (!confirm('Are you sure you want to revoke premium access for this user?')) {
        return
      }
      
      try {
        loading.value = true
        await userService.revokePremiumAccess(userId.value)
        premiumStatus.value = await userService.checkPremiumStatus(userId.value)
      } catch (error) {
        console.error('Error revoking premium access:', error)
        alert('Failed to revoke premium access')
      } finally {
        loading.value = false
      }
    }

    const savePremiumChanges = async () => {
      try {
        loading.value = true
        
        const expiresAtISO = editPremiumData.value.expiresAt 
          ? (() => {
              const date = new Date(editPremiumData.value.expiresAt)
              date.setHours(23, 59, 59, 999)
              return date.toISOString()
            })()
          : null
        
        const startsAtISO = editPremiumData.value.expiresAt 
          ? (() => {
              const date = new Date(editPremiumData.value.expiresAt)
              date.setDate(date.getDate() - 1)
              date.setHours(0, 0, 0, 0)
              return date.toISOString()
            })()
          : null
        
        if (premiumStatus.value?.expiresAt && expiresAtISO) {
          const currentExpiresDate = new Date(premiumStatus.value.expiresAt)
          const newExpiresDate = new Date(expiresAtISO)
          
          if (newExpiresDate < currentExpiresDate) {
            await userService.revokePremiumAccess(userId.value)
          }
        }
        
        const response = await userService.updatePremiumExpiration(
          userId.value,
          expiresAtISO,
          startsAtISO
        )
        
        if (response && response.data) {
          premiumStatus.value = await userService.checkPremiumStatus(userId.value)
        }
        
        isEditingPremium.value = false
      } catch (error) {
        console.error('Error updating premium status:', error)
        alert('Failed to update premium status')
      } finally {
        loading.value = false
      }
    }

    watch(() => route.params.id, (newId) => {
      if (newId) {
        userId.value = newId
        loadData()
      }
    })

    onMounted(() => {
      loadData()
    })

    return {
      userData,
      scans,
      chats,
      loading,
      activeTab,
      premiumStatus,
      isEditingPremium,
      editPremiumData,
      getUserInitials,
      formatCreatedAt,
      formatLastScanDate,
      getLabelColor,
      copyUserId,
      handleImageError,
      expandScan,
      collapseScan,
      formatMessageContent,
      startEditPremium,
      cancelEditPremium,
      savePremiumChanges,
      revokePremium
    }
  }
}
</script>

<style scoped>
.user-detail {
  padding: 24px;
  height: 90dvh;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.detail-header {
  margin-bottom: 24px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.loading-state.full-page,
.error-state.full-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: var(--text-secondary);
}

.cosmic-loader {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state h2 {
  color: var(--error-color);
  margin: 16px 0 8px 0;
}

.back-link {
  margin-top: 16px;
  padding: 12px 24px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-link:hover {
  background: var(--primary-light);
  transform: translateY(-2px);
}

.detail-content {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 30px;
  align-items: flex-start;
}

@media (max-width: 1200px) {
  .detail-content {
    grid-template-columns: 320px 1fr;
  }
}

@media (max-width: 992px) {
  .detail-content {
    grid-template-columns: 1fr;
  }
}

.user-info-card {
  background-color: var(--background-secondary);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid var(--border-color);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.user-info-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.scan-preview {
  margin-top: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.scan-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.summary-item {
  text-align: center;
  padding: 8px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.summary-item strong {
  color: var(--text-primary);
  font-size: 12px;
  display: block;
  margin-bottom: 4px;
}

.see-more-btn,
.see-less-btn {
  width: 100%;
  padding: 12px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.see-more-btn:hover,
.see-less-btn:hover {
  background: var(--primary-light);
  transform: translateY(-1px);
}

.see-less-btn {
  background: var(--text-muted);
  margin-top: 16px;
}

.see-less-btn:hover {
  background: #6c757d;
}

.scan-details-expanded {
  margin-top: 16px;
}

.no-data {
  color: var(--text-muted);
  font-style: italic;
  padding: 8px;
  text-align: center;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  border: 1px dashed var(--border-color);
}

.user-details h2 {
  color: var(--text-primary);
  margin: 0 0 4px 0;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.user-meta {
  color: var(--text-muted);
  font-size: 14px;
}

.info-section {
  margin-bottom: 24px;
}

.info-section h3 {
  color: var(--text-primary);
  margin: 0 0 16px 0;
  font-size: 18px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item .label {
  color: var(--text-muted);
  font-size: 14px;
}

.info-item .value {
  color: var(--text-secondary);
  font-weight: 500;
}

.info-item .value.premium-active {
  color: var(--accent-color);
  font-weight: 600;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  margin: 0;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.edit-btn,
.revoke-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.edit-btn {
  background: var(--primary-color);
  color: var(--text-primary);
}

.edit-btn:hover {
  background: var(--primary-light);
  transform: translateY(-1px);
}

.revoke-btn {
  background: var(--error-color);
  color: var(--text-primary);
}

.revoke-btn:hover {
  background: #c0392b;
  transform: translateY(-1px);
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
}

.form-input {
  padding: 10px 12px;
  background: var(--background-light);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(18, 128, 120, 0.1);
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.btn-save,
.btn-cancel {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-save {
  background: var(--accent-color);
  color: var(--text-primary);
}

.btn-save:hover {
  background: var(--accent-light);
  transform: translateY(-1px);
}

.btn-cancel {
  background: var(--background-light);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.05);
}

.user-id-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-id {
  font-family: monospace;
  background: rgba(255, 255, 255, 0.05);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  flex: 1;
}

.copy-btn {
  background: var(--primary-color);
  border: none;
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  background: var(--primary-light);
  transform: scale(1.05);
}

.scans-history {
  background-color: var(--background-secondary);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-color);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.scans-history h3 {
  color: var(--text-primary);
  margin: 0 0 24px 0;
  font-size: 20px;
}

.scans-container {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 8px;
}

.scan-card {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.scan-card:hover {
  background: rgba(255, 255, 255, 0.05);
  transform: translateY(-2px);
}

.scan-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.scan-info h4 {
  color: var(--text-primary);
  margin: 0 0 4px 0;
  font-size: 16px;
}

.scan-date {
  color: var(--text-muted);
  font-size: 14px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.found {
  background: var(--success-color);
  color: white;
}

.status-badge.not_found {
  background: var(--warning-color);
  color: #333;
}

.scan-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: var(--text-secondary);
  font-size: 14px;
}

.scan-main-info {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: start;
}

.scan-basic-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scan-image {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.scan-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scan-ai-response {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.ai-emoji {
  font-size: 24px;
}

.scan-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.scan-section {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid var(--border-color);
}

.scan-section h5 {
  color: var(--text-primary);
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.allergen-tag {
  background: #ff4757;
  color: white;
}

.category-tag {
  background: #3742fa;
  color: white;
}

.additive-tag {
  background: #ffa502;
  color: #333;
}

.trace-tag {
  background: #2ed573;
  color: white;
}

.label-tag {
  background: #8b5cf6;
  color: white;
}

.ingredients-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ingredient-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.ingredient-name {
  font-weight: 500;
  flex: 1;
}

.ingredient-percent {
  color: var(--text-muted);
  font-size: 12px;
}

.ingredient-badges {
  display: flex;
  gap: 4px;
}

.badge {
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.vegan-badge {
  background: #2ed573;
  color: white;
}

.vegetarian-badge {
  background: #ffa502;
  color: #333;
}

.nutrition-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.nutrition-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  border: 1px solid var(--border-color);
}

.nutrition-label {
  color: var(--text-muted);
  font-size: 12px;
}

.nutrition-value {
  font-weight: 600;
  color: var(--text-secondary);
}

.scores-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.score-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
}

.score-label {
  color: var(--text-muted);
  font-size: 12px;
}

.score-value {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.nova-1 { background: #2ed573; color: white; }
.nova-2 { background: #ffa502; color: #333; }
.nova-3 { background: #ff4757; color: white; }
.nova-4 { background: #8b5cf6; color: white; }

.nutri-a { background: #2ed573; color: white; }
.nutri-b { background: #7bed9f; color: #333; }
.nutri-c { background: #ffa502; color: #333; }
.nutri-d { background: #ff4757; color: white; }
.nutri-e { background: #8b5cf6; color: white; }

.eco-a { background: #2ed573; color: white; }
.eco-b { background: #7bed9f; color: #333; }
.eco-c { background: #ffa502; color: #333; }
.eco-d { background: #ff4757; color: white; }
.eco-e { background: #8b5cf6; color: white; }

.label-reason {
  color: var(--text-muted);
  font-size: 11px;
  font-style: italic;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scan-labels {
  margin-top: 8px;
}

.labels-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.label-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.05);
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
}

.label-emoji {
  font-size: 14px;
}

.label-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.no-scans {
  text-align: center;
  color: var(--text-muted);
  padding: 40px;
}

.chat-history {
  background-color: var(--background-secondary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 8px 24px var(--shadow-color);
  border: 1px solid var(--border-color);
  max-height: 100vh;
  overflow-y: auto;
}

.chats-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.chat-session {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
}

.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 800px;
  overflow-y: auto;
  padding-right: 10px;
}

.chat-message {
  display: flex;
  max-width: 85%;
}

.role-user {
  justify-content: flex-end;
  margin-left: auto;
}

.role-assistant {
  justify-content: flex-start;
}

.message-bubble {
  padding: 12px 18px;
  border-radius: 20px;
  position: relative;
}

.role-user .message-bubble {
  background-color: var(--primary-color);
  color: white;
  border-bottom-right-radius: 5px;
}

.role-assistant .message-bubble {
  background-color: var(--background-light);
  color: var(--text-secondary);
  border-bottom-left-radius: 5px;
}

.message-bubble p {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.message-time {
  display: block;
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 6px;
  text-align: right;
}

.no-chats {
  text-align: center;
  padding: 40px;
  color: var(--text-muted);
}

.history-content {
  background-color: var(--background-secondary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 8px 24px var(--shadow-color);
  border: 1px solid var(--border-color);
  max-height: 100vh;
  overflow-y: auto;
}

.history-switcher {
  display: flex;
  gap: 10px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 16px;
}

.history-switcher button {
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid transparent;
  background-color: transparent;
  color: var(--text-muted);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.history-switcher button:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
}

.history-switcher button.active {
  background-color: var(--primary-color);
  color: white;
  border-color: var(--primary-light);
}

.chat-history {
  /* background-color, padding, etc. are now on .history-content */
}

.scans-history {
  /* background-color, padding, etc. are now on .history-content */
}

.chats-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.chat-session {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
}

.chat-messages {
  max-height: 800px;
}

.scans-container {
  max-height: 800px;
}

@media (max-width: 1024px) {
  .user-detail {
    padding: 16px;
    height: calc(100vh - 60px);
  }

  .detail-content {
    grid-template-columns: 1fr;
  }

  .user-info-card {
    order: -1;
  }

  .scans-history {
    min-height: 70vh;
  }
}

@media (max-width: 768px) {
  .user-detail {
    padding: 16px;
  }
  
  .detail-content {
    gap: 16px;
  }
  
  .user-info-card,
  .scans-history {
    padding: 16px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .scan-header {
    flex-direction: column;
    gap: 8px;
  }

  .scan-main-info {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .scan-image {
    width: 100px;
    height: 100px;
    align-self: center;
  }

  .scan-details-grid {
    grid-template-columns: 1fr;
  }

  .nutrition-grid,
  .scores-grid {
    grid-template-columns: 1fr;
  }

  .ingredient-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .ingredient-badges {
    align-self: flex-end;
  }
}
</style> 